import { useState } from 'react';
import { GitBranch, Tag, ChevronDown, ChevronUp, Pencil, Check, Zap, ListOrdered, ShieldAlert } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import type { ProposedWorkflow } from '@/types/plan';
import { normalizeAutomationProposal } from '@/services/automation/automationProposalSafety';

interface WorkflowPreviewCardProps {
  workflow: ProposedWorkflow;
  onUpdate: (workflow: ProposedWorkflow) => void;
  onCreateDraft: () => void;
  onCreateAndPublish: () => void;
  isCreating?: boolean;
}

export function WorkflowPreviewCard({
  workflow,
  onUpdate,
  onCreateDraft,
  onCreateAndPublish,
  isCreating,
}: WorkflowPreviewCardProps) {
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const commitEdit = (field: keyof ProposedWorkflow) => {
    onUpdate(normalizeAutomationProposal({ ...workflow, [field]: editValue }));
    setEditingField(null);
  };

  const triggerColors: Record<string, string> = {
    mqtt: '#8B5CF6',
    cron: '#F59E0B',
    webhook: '#3B82F6',
    device_data: '#10B981',
    manual: MID_GRAY.DEFAULT,
    state_change: '#EF4444',
  };

  const steps = workflow.steps || [];
  const hasConfig = workflow.triggerConfig && Object.keys(workflow.triggerConfig).length > 0;
  const safety = normalizeAutomationProposal(workflow).safety;
  const canPublish = safety?.approvalRequired !== true && safety?.riskLevel !== 'blocked';
  const riskTone = safety?.riskLevel === 'blocked'
    ? { border: '#FCA5A5', bg: '#FEF2F2', text: '#991B1B' }
    : safety?.riskLevel === 'review_required'
      ? { border: '#FCD34D', bg: '#FFFBEB', text: '#92400E' }
      : { border: '#A7F3D0', bg: '#ECFDF5', text: '#065F46' };

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: `${FOREST_GREEN.DEFAULT}08`,
          borderBottom: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <GitBranch style={{ width: 16, height: 16, color: FOREST_GREEN.DEFAULT }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: FOREST_GREEN.DEFAULT }}>
          Workflow Preview
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Name
          </label>
          {editingField === 'name' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 14 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('name')}
              />
              <button aria-label="Save workflow name" onClick={() => commitEdit('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{workflow.name}</span>
              <button
                aria-label="Edit workflow name"
                onClick={() => startEdit('name', workflow.name)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Description
          </label>
          {editingField === 'description' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 13 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('description')}
              />
              <button aria-label="Save workflow description" onClick={() => commitEdit('description')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: MID_GRAY.dark }}>{workflow.description}</span>
              <button
                aria-label="Edit workflow description"
                onClick={() => startEdit('description', workflow.description)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Trigger + Tags row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {workflow.triggerType && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 10,
                background: `${triggerColors[workflow.triggerType] || MID_GRAY.DEFAULT}15`,
                color: triggerColors[workflow.triggerType] || MID_GRAY.DEFAULT,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Zap style={{ width: 10, height: 10 }} />
              {workflow.triggerType}
            </span>
          )}
          {workflow.tags?.map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 11,
                padding: '2px 6px',
                borderRadius: 10,
                background: `${MID_GRAY.DEFAULT}10`,
                color: MID_GRAY.DEFAULT,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Tag style={{ width: 10, height: 10 }} />
              {tag}
            </span>
          ))}
        </div>

        {safety && (
          <div
            style={{
              border: `1px solid ${riskTone.border}`,
              background: riskTone.bg,
              borderRadius: 6,
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: riskTone.text }}>
              <ShieldAlert style={{ width: 14, height: 14, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>
                {safety.riskLevel.replace('_', ' ')}
              </span>
              <span style={{ fontSize: 12 }}>
                {safety.approvalRequired ? 'Approval required' : 'No approval required'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: MID_GRAY.dark, lineHeight: 1.45 }}>
              <div><strong>Trigger:</strong> {safety.triggerSummary}</div>
              {safety.conditionSummary && <div><strong>Conditions:</strong> {safety.conditionSummary}</div>}
              <div><strong>Action:</strong> {safety.actionSummary}</div>
              {safety.scheduleSummary && <div><strong>Schedule:</strong> {safety.scheduleSummary}</div>}
              {(safety.affectedDevices.length > 0 || safety.affectedEntities.length > 0) && (
                <div>
                  <strong>Affected:</strong> {[...safety.affectedDevices, ...safety.affectedEntities].join(', ')}
                </div>
              )}
              <div><strong>Default:</strong> {safety.draftByDefault || safety.disabledByDefault ? 'Draft and disabled before review' : 'Can remain low-risk draft or be published by user action'}</div>
              <div><strong>Rollback:</strong> {safety.rollbackNote}</div>
            </div>
            {safety.riskReasons.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18, color: riskTone.text, fontSize: 12, lineHeight: 1.4 }}>
                {safety.riskReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Trigger config (collapsible) */}
        {hasConfig && (
          <div>
            <button
              onClick={() => setConfigExpanded(!configExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                color: MID_GRAY.DEFAULT,
                textTransform: 'uppercase',
                padding: 0,
                marginBottom: 4,
              }}
            >
              Trigger Config
              {configExpanded
                ? <ChevronUp style={{ width: 12, height: 12 }} />
                : <ChevronDown style={{ width: 12, height: 12 }} />
              }
            </button>
            {configExpanded && (
              <pre
                style={{
                  fontSize: 11,
                  lineHeight: 1.5,
                  background: `${MID_GRAY.DEFAULT}06`,
                  padding: 10,
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 120,
                  overflow: 'auto',
                  margin: 0,
                }}
              >
                {JSON.stringify(workflow.triggerConfig, null, 2)}
              </pre>
            )}
          </div>
        )}

        {/* Steps (collapsible) */}
        {steps.length > 0 && (
          <div>
            <button
              onClick={() => setStepsExpanded(!stepsExpanded)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                color: MID_GRAY.DEFAULT,
                textTransform: 'uppercase',
                padding: 0,
                marginBottom: 4,
              }}
            >
              Steps ({steps.length})
              {stepsExpanded
                ? <ChevronUp style={{ width: 12, height: 12 }} />
                : <ChevronDown style={{ width: 12, height: 12 }} />
              }
            </button>
            {stepsExpanded && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {steps.map((step, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      fontSize: 12,
                      color: MID_GRAY.dark,
                      padding: '6px 8px',
                      borderRadius: 6,
                      background: '#F9FAFB',
                      border: `1px solid ${BORDER.DEFAULT}`,
                    }}
                  >
                    <ListOrdered style={{ width: 12, height: 12, color: MID_GRAY.DEFAULT, marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <span style={{ fontWeight: 500 }}>{step.type}</span>
                      {step.description && (
                        <span style={{ color: MID_GRAY.DEFAULT }}> — {step.description}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={onCreateDraft}
          disabled={isCreating}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${BORDER.DEFAULT}`,
            background: WHITE,
            fontSize: 13,
            fontWeight: 500,
            cursor: isCreating ? 'not-allowed' : 'pointer',
            opacity: isCreating ? 0.6 : 1,
          }}
        >
          {safety?.approvalRequired ? 'Create Draft for Review' : 'Create as Draft'}
        </button>
        <button
          onClick={onCreateAndPublish}
          disabled={isCreating || !canPublish}
          title={!canPublish ? 'Review-required or blocked automations must be created as disabled drafts first.' : undefined}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            background: FOREST_GREEN.DEFAULT,
            color: WHITE,
            fontSize: 13,
            fontWeight: 500,
            cursor: isCreating || !canPublish ? 'not-allowed' : 'pointer',
            opacity: isCreating || !canPublish ? 0.6 : 1,
          }}
        >
          {isCreating ? 'Creating...' : 'Create & Publish'}
        </button>
      </div>
    </div>
  );
}
